import React, { useContext } from 'react';
import { RatioTableContainer, RatioTable, RatioTableScrollContainer } from '../Styles/SummaryPortfolioModelStyles';
import { ThemeContext } from '@fluentui/react-theme-provider';
import { RatioTablePortfolioModelProps } from '../Interfaces/SummaryPortfolioInterfaces';

type RatioFormatType = 'percentage' | 'weeks' | 'number';
const ratioConfig: Array<{ name: string; format: RatioFormatType }> =
[
    { name: 'linear_return', format: 'percentage' },
    { name: 'volatility', format: 'percentage' },
    { name: 'max_drawdown', format: 'percentage' },
    { name: 'downside_deviation', format: 'percentage' },
    { name: 'time_to_recovery', format: 'weeks' },
    { name: 'omega', format: 'number' },
    { name: 'sharpe', format: 'number' },
    { name: 'calmar', format: 'number' },
    { name: 'sortino', format: 'number' }
];

const RATIO_MAP = new Map(ratioConfig.map((config, index) => [config.name, { order: index, format: config.format }]));

const RatioTablePortfolioModel: React.FC<RatioTablePortfolioModelProps> = ({ratiosData, selectedPeriod, isLoading = false}) =>
{
    const theme = useContext(ThemeContext);
    
    // Función para formatear el período a mayúsculas
    const formatPeriod = (period: string): string =>
    {
        if(period === 'inception')
        {
            return 'Todo';
        }
        return period.toUpperCase();
    };

    // Función helper para validar si un valor es NaN (numérico o string)
    const isValidNumber = (value: any): boolean =>
    {
        // Si es undefined o null, no es válido
        if(value === undefined || value === null)
        {
            return false;
        }
        
        // Si es string "NaN", no es válido
        if(typeof value === 'string' && (value.toLowerCase() === 'nan' || value.trim() === ''))
        {
            return false;
        }
        
        // Convertir a número si es string
        const numValue = typeof value === 'string' ? parseFloat(value) : value;
        
        // Verificar si es NaN numérico
        if(isNaN(numValue) || !isFinite(numValue))
        {
            return false;
        }
        
        return true;
    };
    
    // Función para convertir y normalizar el valor a número
    const normalizeToNumber = (value: any): number =>
    {
        if(typeof value === 'string')
        {
            return parseFloat(value);
        }
        return value;
    };
    
    // Función para formatear el valor según el tipo de ratio
    const formatValue = (value: number, formatType: RatioFormatType): string =>
    {
        // Validar primero si el valor es válido
        if(!isValidNumber(value))
        {
            return "No disponible";
        }
        
        // Normalizar a número
        const numValue = normalizeToNumber(value);
        
        switch(formatType)
        {
            case 'percentage':
                return numValue >= 0 
                    ? `+${(numValue * 100).toFixed(2).replace('.', ',')}%` 
                    : `${(numValue * 100).toFixed(2).replace('.', ',')}%`;
            
            case 'weeks':
                const weeks = numValue.toFixed(0);
                return weeks === '1' ? '1 semana' : `${weeks} semanas`;
            
            case 'number':
                return numValue.toFixed(2).replace('.', ',');

            default:
                return 'N/A';
        }
    };
    
    // Función para ordenar los ratios según el orden especificado
    const sortRatiosByOrder = (ratios: any[]) =>
    {
        return ratios.sort((a, b) =>
        {
            const configA = RATIO_MAP.get(a.ratioName);
            const configB = RATIO_MAP.get(b.ratioName);
            
            // Si ambos están en la configuración, ordenar por su índice
            if(configA && configB)
            {
                return configA.order - configB.order;
            }
            
            // Si solo uno está en la configuración, ponerlo primero
            if(configA)
            {
                return -1;
            }
            if(configB)
            {
                return 1;
            }
            
            // Si ninguno está en la configuración, mantener orden original
            return 0;
        });
    };

    if(isLoading)
    {
        return(
            <RatioTableContainer>
                <RatioTableScrollContainer>
                    <RatioTable theme={theme}>
                        <thead>
                            <tr>
                                <th>Ratios ({formatPeriod(selectedPeriod)})</th>
                                <th>Valores</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>Cargando...</td>
                                <td>-</td>
                            </tr>
                        </tbody>
                    </RatioTable>
                </RatioTableScrollContainer>
            </RatioTableContainer>
        );
    }

    if(!ratiosData || !ratiosData.ratios || ratiosData.ratios.length === 0)
    {
        return(
            <RatioTableContainer>
                <RatioTableScrollContainer>
                    <RatioTable theme={theme}>
                        <thead>
                            <tr>
                                <th>Ratios ({formatPeriod(selectedPeriod)})</th>
                                <th>Valores</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>No hay datos disponibles</td>
                                <td>-</td>
                            </tr>
                        </tbody>
                    </RatioTable>
                </RatioTableScrollContainer>
            </RatioTableContainer>
        );
    }

    return(
        <RatioTableContainer>
            <RatioTableScrollContainer>
                <RatioTable theme={theme}>
                    <thead>
                        <tr>
                            <th>Ratios ({formatPeriod(selectedPeriod)})</th>
                            <th>Valores</th>
                        </tr>
                    </thead>
                    <tbody>
                        {sortRatiosByOrder([...ratiosData.ratios]).map((ratio, index) =>
                        {
                            const value = ratio.values[selectedPeriod];
                            const config = RATIO_MAP.get(ratio.ratioName);
                            
                            const displayValue = value !== undefined && config
                                                ? formatValue(value, config.format)
                                                : 'N/A';
                            
                            return(
                                <tr key={`${ratio.ratioName}-${index}`}>
                                    <td>{ratio.ratioDisplayName}</td>
                                    <td>{displayValue}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </RatioTable>
            </RatioTableScrollContainer>
        </RatioTableContainer>
    );
};

export default RatioTablePortfolioModel;